import { SessionType, type SessionTypeKey } from '@/stores/sessionType'

import { CITY_RUSTIC_SCENE_IDS, DESTINATION_SCENE_IDS, OUTDOOR_SCENE_ID } from './upgrades'

// === RS+ School Scene IDs ===

/**
 * The four named scenes RS+ schools may select from (plus yearbook, which is
 * always auto-included). Studio-only scenes are not shown at school — the
 * Scene Selection view filters the studio group down to these IDs when
 * `isRoadshowPlusFlow && selectedLocationType === School`.
 *
 * Per RS+ Flow Updates (260412): "RS+ School Scenes Limited to 4 Named
 * Scenes" — explicit list replaces the earlier "limited scenes shown due to
 * fewer available at a roadshow" understanding.
 */
export const RS_PLUS_SCHOOL_SCENE_IDS = ['exposure', 'timeless', 'emerge', 'living-walls'] as const

/** Scene IDs considered black-and-white in the RS+ school scenes subset. */
const RS_PLUS_BW_SCHOOL_SCENE_IDS = ['exposure'] as const

/** True when the given scene ID is a B&W scene in the RS+ school scenes subset. */
export function isRsPlusBwSchoolScene(sceneId: string): boolean {
  return (RS_PLUS_BW_SCHOOL_SCENE_IDS as readonly string[]).includes(sceneId)
}

// === RS+ sibling key resolver ===

/**
 * Map each standard tier key to its RS+ sibling. Used at selection entry
 * points (Sessions Listing card clicks, etc.) to bind the RS+ DTO when
 * the user is on an RS+ school with the School location toggle active.
 * Per the Flow Updates guide § Catalog API Support for RS+ — premium
 * tiers (MP / Elite / EPlus / EU) and `YearbookOnly` have no RS+ sibling
 * and are absent from this map.
 */
const RS_PLUS_SIBLING_KEYS: Partial<Record<SessionTypeKey, SessionTypeKey>> = {
  [SessionType.Legacy]: SessionType.LegacyRoadshowPlus,
  [SessionType.Standard]: SessionType.StandardRoadshowPlus,
  [SessionType.Simple]: SessionType.SimpleRoadshowPlus,
  [SessionType.Basic]: SessionType.BasicRoadshowPlus,
  [SessionType.BasicPlus]: SessionType.BasicPlusRoadshowPlus,
  [SessionType.BasicMax]: SessionType.BasicMaxRoadshowPlus,
  [SessionType.Mini]: SessionType.MiniRoadshowPlus,
  [SessionType.Essential]: SessionType.EssentialRoadshowPlus
}

/**
 * Returns the RS+ sibling session type key for a standard tier, or
 * `null` if no RS+ variant exists (premium tiers + YearbookOnly).
 *
 * Callers should only consult this when the user is on an RS+ school
 * with `selectedLocationType === School`; elsewhere the standard key is
 * correct.
 */
export function getRoadshowPlusSiblingKey(key: SessionTypeKey): SessionTypeKey | null {
  return RS_PLUS_SIBLING_KEYS[key] ?? null
}

/** True when the given key is one of the 8 `*RoadshowPlus` session types. */
export function isRoadshowPlusKey(key: SessionTypeKey): boolean {
  return Object.values(RS_PLUS_SIBLING_KEYS).includes(key)
}

/**
 * Returns the standard sibling key for an RS+ session type (inverse of
 * `getRoadshowPlusSiblingKey`), or null if the key isn't an RS+ tier.
 *
 * Intended for content-key lookups where RS+ tiers reuse their studio
 * sibling's CMS entry (e.g. the `BUNDLE_UPGRADE_MODAL` switch in
 * `SessionsListingPageView`). Not a flow-control helper — RS+ users
 * accepting an upgrade still land on the RS+ target, not its studio sibling.
 */
export function getStudioSiblingKey(key: SessionTypeKey): SessionTypeKey | null {
  for (const [studioKey, rsPlusKey] of Object.entries(RS_PLUS_SIBLING_KEYS)) {
    if (rsPlusKey === key) return studioKey as SessionTypeKey
  }
  return null
}

// === RS+ at-school upgrade targets ===

/**
 * Maps each RS+ session type key to its at-school upgrade target — the
 * next `*RoadshowPlus` tier when a user upgrades while `selectedLocationType`
 * is `School`. Sibling to `upgradeTargetKeyMap` in `./upgrades.ts`; the
 * split keeps RS+ logic out of the generic helper per ADR 2026.04.20-002.
 *
 * Budget-tier siblings mirror their standard-flow counterparts in
 * `upgradeTargetKeyMap`: BasicPlus/Mini upgrade like Simple (→ Standard) and
 * BasicMax/Essential upgrade like Standard (→ Legacy). Surfaces the same
 * listing-page upgrade modal on Continue that the studio flow already shows
 * for these tiers.
 *
 * Studio-exit mappings (where a `*RoadshowPlus` key maps back to a standard
 * or premium tier while `selectedLocationType` flips to `Studio`) land in
 * Iteration 4b — they are NOT part of this map.
 */
const roadshowUpgradeTargetKeyMap: Partial<Record<SessionTypeKey, SessionTypeKey>> = {
  [SessionType.BasicRoadshowPlus]: SessionType.SimpleRoadshowPlus,
  [SessionType.BasicPlusRoadshowPlus]: SessionType.StandardRoadshowPlus,
  [SessionType.BasicMaxRoadshowPlus]: SessionType.LegacyRoadshowPlus,
  [SessionType.MiniRoadshowPlus]: SessionType.StandardRoadshowPlus,
  [SessionType.EssentialRoadshowPlus]: SessionType.LegacyRoadshowPlus,
  [SessionType.SimpleRoadshowPlus]: SessionType.StandardRoadshowPlus,
  [SessionType.StandardRoadshowPlus]: SessionType.LegacyRoadshowPlus
}

/**
 * Returns the RS+ at-school upgrade target session type key for a given
 * source key, or null if no at-school upgrade path exists. Callers should
 * only consult this map when `isRoadshowPlusFlow && selectedLocationType === School`.
 */
export function getRoadshowUpgradeTargetKey(key: SessionTypeKey): SessionTypeKey | null {
  return roadshowUpgradeTargetKeyMap[key] ?? null
}

// === RS+ studio-exit matrix ===
//
// When an RS+ user triggers any Studio exit (Ad Space, See the Difference,
// studio-scene click, outdoor/city/destination click), both the session
// type key and `selectedLocationType` must flip atomically. Sibling to the
// generic scene-upgrade matrix in `./upgrades.ts`; the split keeps RS+
// keys out of that shared helper per ADR 2026.04.20-002 Pattern 1.

/**
 * Tier-preserving studio switch: maps each `*RoadshowPlus` key to its direct
 * standard sibling without changing tier. Drives three exits today:
 *   - Scene Selection — Ad Space and "See the Difference" CTAs
 *   - Checkout Summary — RS+ Studio Upgrade green bar (`CheckoutSummaryView`,
 *     for traditional and value tiers per `RS_PLUS_STUDIO_UPGRADE_BAR_KEYS`)
 *
 * `BasicRoadshowPlus` is mapped for completeness — the Scene Selection exits
 * render above the school-scenes section so they don't fire at Basic, but the
 * Checkout Summary value-tier upgrade flow is reachable from Basic-tier siblings.
 *
 * `MiniRoadshowPlus` / `EssentialRoadshowPlus` are mapped preemptively. Today
 * those keys are filtered out of `catalogWithContent` upstream because
 * `sessionBundleFallbacks` lacks `Mini` / `Essential` entries, so the bar
 * never actually shows for them — the entries here are dead branches until
 * Mini/Essential rendering is wired up, at which point this resolver is
 * already ready.
 */
const roadshowStudioSwitchKeepTierMap: Partial<Record<SessionTypeKey, SessionTypeKey>> = {
  [SessionType.BasicRoadshowPlus]: SessionType.Basic,
  [SessionType.SimpleRoadshowPlus]: SessionType.Simple,
  [SessionType.StandardRoadshowPlus]: SessionType.Standard,
  [SessionType.LegacyRoadshowPlus]: SessionType.Legacy,
  [SessionType.BasicPlusRoadshowPlus]: SessionType.BasicPlus,
  [SessionType.BasicMaxRoadshowPlus]: SessionType.BasicMax,
  [SessionType.MiniRoadshowPlus]: SessionType.Mini,
  [SessionType.EssentialRoadshowPlus]: SessionType.Essential
}

/**
 * Studio-only scene click, user has NO school scenes selected. Preserves
 * the at-school tier's studio sibling. `BasicRoadshowPlus` maps up one
 * tier to `Simple` since Basic has no scene-selectable studio parallel,
 * and `LegacyRoadshowPlus` maps to `MostPopular` since Legacy is the top
 * at-school tier and clicking a studio scene implies wanting more.
 */
const roadshowStudioSceneClickKeepTierMap: Partial<Record<SessionTypeKey, SessionTypeKey>> = {
  [SessionType.BasicRoadshowPlus]: SessionType.Simple,
  [SessionType.SimpleRoadshowPlus]: SessionType.Simple,
  [SessionType.StandardRoadshowPlus]: SessionType.Standard,
  [SessionType.LegacyRoadshowPlus]: SessionType.MostPopular
}

/**
 * Studio-only scene click, user HAS school scenes selected. Treated as
 * "I want these school scenes + a studio scene" — tier-bumps at studio so
 * the bundle has room for the extra scene.
 */
const roadshowStudioSceneClickTierBumpMap: Partial<Record<SessionTypeKey, SessionTypeKey>> = {
  [SessionType.BasicRoadshowPlus]: SessionType.Simple,
  [SessionType.SimpleRoadshowPlus]: SessionType.Standard,
  [SessionType.StandardRoadshowPlus]: SessionType.Legacy,
  [SessionType.LegacyRoadshowPlus]: SessionType.MostPopular
}

/**
 * Resolve the studio-exit target session type for a tier-preserving
 * switch — Ad Space click, See the Difference click, or any other
 * "go to studio flow without changing tier" trigger.
 *
 * @returns Standard-sibling `SessionTypeKey` for the exit, or `null` if
 *          the current key has no mapped switch target (e.g. a non-RS+
 *          key was passed in accidentally).
 */
export function getRoadshowStudioSwitchTargetKey(
  currentRsPlusKey: SessionTypeKey
): SessionTypeKey | null {
  return roadshowStudioSwitchKeepTierMap[currentRsPlusKey] ?? null
}

/**
 * Resolve the studio-exit target session type when an RS+ user clicks a
 * scene that isn't available at school. The target depends on the scene
 * category and — for plain studio scenes — whether the user already has
 * school scenes selected.
 *
 * Categories:
 * - **Destination** (Choose Your Destination, Lake): always `EliteUnlimited`
 * - **City / Rustic**: always `Elite`
 * - **Outdoor**: always `MostPopular`
 * - **Studio-only** (non-school studio scenes): depends on
 *   `hasSelectedSchoolScenes` — keep-tier studio sibling if false,
 *   tier-bumped studio sibling if true
 *
 * @returns Target `SessionTypeKey`, or `null` if the current RS+ key is
 *          unmapped (should not happen for valid RS+ flows).
 */
export function getRoadshowStudioSceneExitTargetKey(
  currentRsPlusKey: SessionTypeKey,
  sceneId: string,
  hasSelectedSchoolScenes: boolean
): SessionTypeKey | null {
  if (DESTINATION_SCENE_IDS.includes(sceneId)) {
    return SessionType.EliteUnlimited
  }
  if (CITY_RUSTIC_SCENE_IDS.includes(sceneId)) {
    return SessionType.Elite
  }
  if (sceneId === OUTDOOR_SCENE_ID) {
    return SessionType.MostPopular
  }
  const map = hasSelectedSchoolScenes
    ? roadshowStudioSceneClickTierBumpMap
    : roadshowStudioSceneClickKeepTierMap
  return map[currentRsPlusKey] ?? null
}
